import classNames from 'classnames';
import { IVisibleLobby } from '../../../Models/LobbyModels';
import Avatar from '../Avatar/Avatar';
import {
  BUTTON_TEXT_CLASS_NAME,
  CARD_CLASS_NAME,
  FOCUS_CLASS_NAME,
} from '../StartScreen/styles';

interface ILobbyListItemProps {
  lobby: IVisibleLobby;
  disabled: boolean;
  joinLobby: (lobbyId: string) => void;
}

const MAX_AVATARS = 3;

const STACKED_CLASS_NAME =
  '-ml-[8px] size-[28px] ring-2 ring-surface first:ml-0';

function plural(count: number, word: string) {
  return `${count} ${word}${count === 1 ? '' : 's'}`;
}

export function playerCountText(playerCount: number, botCount: number) {
  const players = plural(playerCount, 'player');
  return botCount > 0 ? `${players} · ${plural(botCount, 'bot')}` : players;
}

function AvatarStack({ lobby }: { lobby: IVisibleLobby }) {
  const shown = lobby.players.slice(0, MAX_AVATARS);
  const hiddenCount = lobby.players.length - shown.length;
  return (
    <span aria-hidden="true" className="flex shrink-0">
      {shown.map((player, i) => (
        <Avatar
          key={player.idHash}
          name={player.name}
          colorIndex={i}
          className={classNames(STACKED_CLASS_NAME, 'text-[12px]')}
        />
      ))}
      {hiddenCount > 0 && (
        <span
          className={classNames(
            STACKED_CLASS_NAME,
            'flex items-center justify-center rounded-full bg-page-mid text-[11px] font-bold text-ink-muted'
          )}
        >
          +{hiddenCount}
        </span>
      )}
    </span>
  );
}

export default function LobbyListItem({
  lobby,
  disabled,
  joinLobby,
}: ILobbyListItemProps) {
  const isInGame = !!lobby.additionalGameProperties.currentGameId;
  return (
    <li>
      <button
        onClick={() => joinLobby(lobby.id)}
        disabled={disabled}
        className={classNames(
          CARD_CLASS_NAME,
          FOCUS_CLASS_NAME,
          'flex w-full items-center gap-[10px] py-[12px] pr-[10px] pl-[16px] text-ink transition-[scale] duration-150 active:scale-[0.98]'
        )}
      >
        <span
          className={classNames(
            'flex min-w-0 flex-1 flex-col gap-[2px] text-left',
            BUTTON_TEXT_CLASS_NAME
          )}
        >
          <span className="flex min-w-0 items-center gap-[6px]">
            <span className="truncate text-[15px] font-semibold">
              {lobby.name}
            </span>
            {isInGame && (
              <span className="shrink-0 rounded-full bg-straw px-[8px] py-[2px] text-[11px] font-bold text-straw-ink">
                In game
              </span>
            )}
          </span>
          <span className="truncate text-[12px] font-medium text-ink-muted">
            {playerCountText(
              lobby.players.length,
              lobby.additionalGameProperties.numberOfBots
            )}
          </span>
        </span>
        <AvatarStack lobby={lobby} />
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="shrink-0 text-ink-muted"
        >
          <path d="M9 6l6 6-6 6" />
        </svg>
      </button>
    </li>
  );
}
